import { useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import NotFoundPage from "components/error/NotFoundPage";
import NoAccessPage from "components/error/NoAccessPage";
import PageLoading from "components/loading/PageLoading";
import FullScreenModal from "ds/components/FullScreenModal";
import useErrorHandle from "hooks/useErrorHandle";
import useCanCreateTemplateDeployment from "shared/Templates/access/useCanCreateTemplateDeployment";

import useTemplateVersion from "../useTemplateVersion";
import DeployForm from "./DeployForm";

const Deploy = () => {
  const { versionId, templateId } = useParams<{ versionId: string; templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { error, loading, data } = useTemplateVersion(templateId, versionId);
  const template = data?.blueprintVersionedGroup;

  const canDeployTemplate = useCanCreateTemplateDeployment();

  useErrorHandle(error);

  // Check URL search params for marketplace context (persists through refresh)
  const searchParams = new URLSearchParams(location.search);
  const isFromMarketplace = searchParams.get("from") === "marketplace";

  const backLinkUrl = isFromMarketplace
    ? "/templates"
    : `/template/${templateId}/version/${versionId}/deployments`;

  const backUrl = getBreadcrumbsBackUrl(backLinkUrl, "");

  const handleClose = useCallback(() => {
    navigate(backUrl);
  }, [navigate, backUrl]);

  if (loading) {
    return <PageLoading />;
  }

  if (!template?.blueprintVersion || !templateId || !versionId) {
    return <NotFoundPage />;
  }

  // Check if user has permission to deploy
  if (!canDeployTemplate.result) {
    return <NoAccessPage />;
  }

  return (
    <FullScreenModal title={`Deploy ${template.name} template`} onClose={handleClose}>
      <DeployForm
        version={template.blueprintVersion}
        templateName={template.name}
        templateId={templateId}
        isFromMarketplace={isFromMarketplace}
      />
    </FullScreenModal>
  );
};

export default Deploy;
